import { useState, useEffect, useCallback } from 'react';
import { UserGroup } from '../types';
import { getTenantGroups } from '../services/api';

// Reserved route segments that should not be treated as client IDs
const RESERVED_ROUTES = ['onboard', 'new', 'create', 'edit'];

interface UseGroupsResult {
  groups: UserGroup[];
  groupCount: number;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useGroups(clientId: string | undefined): UseGroupsResult {
  const [groups, setGroups] = useState<UserGroup[]>([]);
  const [groupCount, setGroupCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGroups = useCallback(async () => {
    if (!clientId || RESERVED_ROUTES.includes(clientId.toLowerCase())) {
      setGroups([]);
      setGroupCount(0);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getTenantGroups(clientId);
      setGroups(response.groups);
      setGroupCount(response.groupCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch groups');
      setGroups([]);
      setGroupCount(0);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  return {
    groups,
    groupCount,
    loading,
    error,
    refetch: fetchGroups,
  };
}
